import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { z } from 'zod';
import { assetsWarehouseLocationIdSchema } from './assets-warehouse.contract.js';
export const assetsInventoryCapability = 'assets.inventory.v1';
export const assetsInventoryMovementsCapability = 'assets.inventory-movements.v1';
export const assetsInventoryCostSummariesCapability = 'assets.inventory-cost-summaries.v1';
export const assetsInventoryItemIdSchema = z.string().uuid();
export const assetsInventoryMovementIdSchema = z.string().uuid();
export const assetsInventoryCategorySchema = z.enum(['consumable', 'tool']);
export const assetsInventoryUnitSchema = z.enum([
    'each', 'box', 'roll', 'gallon', 'bag', 'sheet', 'pair', 'foot', 'pound', 'ounce', 'liter',
]);
export const assetsInventoryMovementKindSchema = z.enum([
    'checkout-consumable', 'checkout-tool', 'return-consumable', 'return-tool', 'restock',
]);
export const assetsInventoryMovementItemReferenceSchema = z.strictObject({
    itemId: assetsInventoryItemIdSchema,
    sku: z.string().trim().min(1).max(120),
    name: z.string().trim().min(1).max(240),
    category: assetsInventoryCategorySchema,
    unitOfMeasure: assetsInventoryUnitSchema,
    itemRevision: z.number().int().positive(),
});
const quantityHundredthsSchema = z.number().int().min(0).max(9_999_999_999);
const positiveQuantityHundredthsSchema = z.number().int().min(1).max(9_999_999_999);
const moneyCentsSchema = z.number().int().min(0).max(999_999_999_999);
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
export const assetsInventoryItemInputSchema = z.strictObject({
    sku: z.string().trim().min(1).max(120),
    name: z.string().trim().min(1).max(240),
    category: assetsInventoryCategorySchema,
    unitOfMeasure: assetsInventoryUnitSchema,
    unitCostCents: moneyCentsSchema,
    reorderThresholdHundredths: quantityHundredthsSchema,
    warehouseLocationId: assetsWarehouseLocationIdSchema.nullable(),
    legacyLocationLabel: nullableText(240),
    notes: nullableText(4_000),
});
export const assetsInventoryItemPublicDataSchema = assetsInventoryItemInputSchema.extend({
    unitCostCents: moneyCentsSchema.nullable(),
});
export const assetsInventoryItemSchema = z.strictObject({
    tenantId: z.string().uuid(),
    id: assetsInventoryItemIdSchema,
    item: assetsInventoryItemPublicDataSchema,
    currentQuantityHundredths: quantityHundredthsSchema,
    outstandingToolQuantityHundredths: quantityHundredthsSchema,
    availableQuantityHundredths: quantityHundredthsSchema,
    lowStock: z.boolean(),
    revision: z.number().int().positive(),
    archivedAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: z.string().uuid(),
    updatedByMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
});
export const assetsInventoryItemVersionSchema = z.strictObject({
    id: z.string().uuid(),
    tenantId: z.string().uuid(),
    itemId: assetsInventoryItemIdSchema,
    revision: z.number().int().positive(),
    operation: z.enum(['create', 'update', 'archive', 'restore']),
    item: assetsInventoryItemInputSchema,
    currentQuantityHundredths: quantityHundredthsSchema,
    archivedAfter: z.boolean(),
    reason: z.string().trim().min(10).max(500),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
});
export const assetsCreateInventoryItemCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: z.string().uuid(),
    itemId: assetsInventoryItemIdSchema,
    item: assetsInventoryItemInputSchema,
    initialQuantityHundredths: quantityHundredthsSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsUpdateInventoryItemCommandSchema = z.strictObject({
    kind: z.literal('update'),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    item: assetsInventoryItemInputSchema,
    reason: z.string().trim().min(10).max(500),
});
export const assetsInventoryItemStateCommandSchema = z.strictObject({
    kind: z.enum(['archive', 'restore']),
    commandId: z.string().uuid(),
    expectedRevision: z.number().int().positive(),
    reason: z.string().trim().min(10).max(500),
});
export const assetsInventoryItemCommandSchema = z.discriminatedUnion('kind', [
    assetsUpdateInventoryItemCommandSchema,
    assetsInventoryItemStateCommandSchema,
]);
export const assetsInventoryItemListQuerySchema = z.strictObject({
    query: z.string().trim().max(160).default(''),
    category: assetsInventoryCategorySchema.nullable().default(null),
    warehouseLocationId: assetsWarehouseLocationIdSchema.nullable().default(null),
    lowStockOnly: z.boolean().default(false),
    includeArchived: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const movementBaseSchema = z.strictObject({
    commandId: z.string().uuid(),
    itemId: assetsInventoryItemIdSchema,
    quantityHundredths: positiveQuantityHundredthsSchema,
    notes: nullableText(2_000),
    reason: z.string().trim().min(10).max(500),
});
export const assetsCheckoutInventoryCommandSchema = movementBaseSchema.extend({
    kind: z.literal('checkout'),
    projectId: z.string().uuid().nullable(),
});
export const assetsReturnInventoryCommandSchema = movementBaseSchema.extend({
    kind: z.literal('return'),
    sourceMovementId: assetsInventoryMovementIdSchema,
});
export const assetsRestockInventoryCommandSchema = movementBaseSchema.extend({
    kind: z.literal('restock'),
    purchaseCostCents: moneyCentsSchema,
    vendor: nullableText(240),
    receiptReference: nullableText(240),
});
export const assetsInventoryMovementCommandSchema = z.discriminatedUnion('kind', [
    assetsCheckoutInventoryCommandSchema,
    assetsReturnInventoryCommandSchema,
    assetsRestockInventoryCommandSchema,
]);
export const assetsInventoryMovementSchema = z.strictObject({
    tenantId: z.string().uuid(),
    id: assetsInventoryMovementIdSchema,
    itemId: assetsInventoryItemIdSchema,
    itemReference: assetsInventoryMovementItemReferenceSchema,
    kind: assetsInventoryMovementKindSchema,
    sourceMovementId: assetsInventoryMovementIdSchema.nullable(),
    quantityHundredths: positiveQuantityHundredthsSchema,
    currentQuantityBeforeHundredths: quantityHundredthsSchema,
    currentQuantityAfterHundredths: quantityHundredthsSchema,
    sourceOutstandingAfterHundredths: quantityHundredthsSchema.nullable(),
    unitCostCents: moneyCentsSchema.nullable(),
    totalCostCents: moneyCentsSchema.nullable(),
    purchaseCostCents: moneyCentsSchema.nullable(),
    projectReference: projectsProjectReferenceSnapshotSchema.nullable(),
    projectAccessDecision: projectsProjectAccessDecisionSchema.nullable(),
    projectAssignmentOverride: z.boolean(),
    thresholdCrossed: z.boolean(),
    vendor: nullableText(240),
    receiptReference: nullableText(240),
    notes: nullableText(2_000),
    actorMembershipId: z.string().uuid(),
    createdAt: z.string().datetime({ offset: true }),
}).superRefine((movement, context) => {
    const hasProject = movement.projectReference !== null;
    if (hasProject !== (movement.projectAccessDecision !== null)) {
        context.addIssue({
            code: 'custom',
            path: ['projectAccessDecision'],
            message: 'Project reference and access decision must be retained together.',
        });
    }
    if (movement.projectAssignmentOverride !==
        (movement.projectAccessDecision?.assignmentOverrideRequired ?? false)) {
        context.addIssue({
            code: 'custom',
            path: ['projectAssignmentOverride'],
            message: 'Inventory Project override must match the authoritative Projects decision.',
        });
    }
});
export const assetsInventoryMovementListQuerySchema = z.strictObject({
    itemId: assetsInventoryItemIdSchema.nullable().default(null),
    projectId: z.string().uuid().nullable().default(null),
    kind: assetsInventoryMovementKindSchema.nullable().default(null),
    outstandingOnly: z.boolean().default(false),
    limit: z.number().int().min(1).max(500).default(100),
    offset: z.number().int().nonnegative().default(0),
});
const commandMetadataSchema = z.strictObject({
    commandId: z.string().uuid(),
    idempotent: z.boolean(),
});
export const assetsInventoryItemsResponseSchema = z.strictObject({
    data: z.array(assetsInventoryItemSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const assetsInventoryItemResponseSchema = z.strictObject({
    data: assetsInventoryItemSchema,
    command: commandMetadataSchema.optional(),
});
export const assetsInventoryItemVersionsResponseSchema = z.strictObject({
    data: z.array(assetsInventoryItemVersionSchema),
});
export const assetsInventoryMovementsResponseSchema = z.strictObject({
    data: z.array(assetsInventoryMovementSchema),
    page: z.strictObject({
        limit: z.number().int().positive(),
        offset: z.number().int().nonnegative(),
        returned: z.number().int().nonnegative(),
        total: z.number().int().nonnegative(),
    }),
});
export const assetsInventoryMovementResponseSchema = z.strictObject({
    data: assetsInventoryMovementSchema,
    item: assetsInventoryItemSchema,
    command: commandMetadataSchema,
});
export const assetsInventoryCostSummaryGroupBySchema = z.enum(['project', 'category']);
export const assetsInventoryCostSummaryQuerySchema = z.strictObject({
    groupBy: assetsInventoryCostSummaryGroupBySchema,
    from: z.string().datetime({ offset: true }).nullable().default(null),
    to: z.string().datetime({ offset: true }).nullable().default(null),
}).refine((value) => value.from === null || value.to === null || value.from <= value.to, {
    message: 'Inventory cost summary from must not be after to.',
    path: ['to'],
});
export const assetsInventoryCostSummaryRowSchema = z.strictObject({
    groupBy: assetsInventoryCostSummaryGroupBySchema,
    groupKey: z.string().trim().min(1).max(500),
    label: z.string().trim().min(1).max(500),
    projectReference: projectsProjectReferenceSnapshotSchema.nullable(),
    category: assetsInventoryCategorySchema.nullable(),
    checkoutQuantityHundredths: quantityHundredthsSchema,
    returnQuantityHundredths: quantityHundredthsSchema,
    netQuantityHundredths: z.number().int().min(-9_999_999_999).max(9_999_999_999),
    checkoutCostCents: moneyCentsSchema,
    returnCostCents: moneyCentsSchema,
    netCostCents: z.number().int().min(-999_999_999_999).max(999_999_999_999),
    movementCount: z.number().int().nonnegative(),
}).superRefine((row, context) => {
    if (row.groupBy === 'project' && row.category !== null) {
        context.addIssue({ code: 'custom', path: ['category'], message: 'Project groups do not carry a category key.' });
    }
    if (row.groupBy === 'category' && (row.category === null || row.projectReference !== null)) {
        context.addIssue({ code: 'custom', path: ['projectReference'], message: 'Category groups carry only a category key.' });
    }
    if (row.netQuantityHundredths !== row.checkoutQuantityHundredths - row.returnQuantityHundredths) {
        context.addIssue({ code: 'custom', path: ['netQuantityHundredths'], message: 'Net quantity must subtract returns.' });
    }
    if (row.netCostCents !== row.checkoutCostCents - row.returnCostCents) {
        context.addIssue({ code: 'custom', path: ['netCostCents'], message: 'Net cost must subtract returns.' });
    }
});
export const assetsInventoryCostSummaryResponseSchema = z.strictObject({
    data: z.strictObject({
        contractVersion: z.literal(assetsInventoryCostSummariesCapability),
        groupBy: assetsInventoryCostSummaryGroupBySchema,
        from: z.string().datetime({ offset: true }).nullable(),
        to: z.string().datetime({ offset: true }).nullable(),
        checkoutCostCents: moneyCentsSchema,
        returnCostCents: moneyCentsSchema,
        netCostCents: z.number().int().min(-999_999_999_999).max(999_999_999_999),
        rows: z.array(assetsInventoryCostSummaryRowSchema),
    }),
});
export function createAssetsInventoryItemDefaults() {
    return {
        sku: '',
        name: '',
        category: 'consumable',
        unitOfMeasure: 'each',
        unitCostCents: 0,
        reorderThresholdHundredths: 500,
        warehouseLocationId: null,
        legacyLocationLabel: null,
        notes: null,
    };
}
//# sourceMappingURL=assets-inventory.contract.js.map